import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { translation, format, formatToReactComponent, TranslatedComponent } from '@hh.ru/front-static-app';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import Button, { ButtonAppearance, ButtonKind, ButtonType } from 'bloko/blocks/button';
import Checkbox from 'bloko/blocks/checkbox';
import ClickInfo, { InfoPlacement, InfoTheme } from 'bloko/blocks/drop/Info/Click';
import BlokoLink from 'bloko/blocks/link';
import Loading from 'bloko/blocks/loading';
import Text from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import Form from 'src/components/Form';
import { replaceAttachedResumes } from 'src/models/careerGuidance';
import { CareerGuidanceState } from 'src/types/applicantServices';
import fetcher from 'src/utils/fetcher';

import styles from './styles.less';

const ATTACH_RESUME_URL = '/applicant-services/career_guidance';

declare global {
    interface FetcherPostApi {
        [ATTACH_RESUME_URL]: {
            body: FormData | { resumeIdentifier: number[]; attachResumeId: number[] };
            queryParams: void;
            response: void;
        };
    }
}

const TrlKeys = {
    attach: 'applicantServices.career_guidance.result.attach',
    attached: 'applicantServices.career_guidance.result.attached',
    attachedOne: 'applicantServices.career_guidance.result.attached.one',
    attachInfo: 'applicantServices.career_guidance.result.attach.info',
    attachSave: 'applicantServices.career_guidance.result.attach.save',
    attachCancel: 'applicantServices.career_guidance.result.attach.cancel',
    noResume: 'applicantServices.career_guidance.noResume',
    noResumeLink: 'applicantServices.career_guidance.noResume.link',
};

const AttachResume: TranslatedComponent<{ resumes?: CareerGuidanceState['resumes']; stretched: boolean }> = ({
    trls,
    resumes,
    stretched,
}) => {
    const [fetching, setFetching] = useState(false);
    const dispatch = useDispatch();

    const hasMoreOneResume = resumes?.resumeIdentifiers.length && resumes?.resumeIdentifiers.length > 1;
    const AttachWrapper = hasMoreOneResume ? ClickInfo : React.Fragment;
    const buttonRef = useRef<HTMLButtonElement>(null);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFetching(true);
        const form = new FormData(event.currentTarget);
        fetcher
            .postFormData(ATTACH_RESUME_URL, new FormData(event.currentTarget))
            .then(() => {
                dispatch(replaceAttachedResumes(form.getAll('attachResumeId').map(Number)));
            })
            .catch((error) => console.error(error))
            .finally(() => {
                setFetching(false);
            });
    };
    const attachToggle = () => {
        if (hasMoreOneResume) {
            return;
        }
        setFetching(true);
        const resumesList = resumes?.resumeIdentifiers.map(({ id }) => id) || [];
        const attachList = resumes?.resumeIdsProfTestAttached.length ? [] : resumesList;
        fetcher
            .postFormData(ATTACH_RESUME_URL, {
                resumeIdentifier: resumesList,
                attachResumeId: attachList,
            })
            .then(() => {
                dispatch(replaceAttachedResumes(attachList));
            })
            .catch((error) => console.error(error))
            .finally(() => {
                setFetching(false);
            });
    };
    const getAttachButtonText = () => {
        if (resumes?.resumeIdsProfTestAttached.length) {
            return resumes.resumeIdentifiers.length === 1
                ? trls[TrlKeys.attachedOne]
                : format(trls[TrlKeys.attached], { '{0}': resumes?.resumeIdsProfTestAttached.length });
        }
        return trls[TrlKeys.attach];
    };

    if (!resumes?.resumeIdentifiers.length) {
        return (
            <div className={styles.textWrapper}>
                <Text data-qa="no-resume-text">
                    {formatToReactComponent(trls[TrlKeys.noResume], {
                        '{0}': (
                            <BlokoLink Element={SPALink} to="applicant/resumes/new">
                                {trls[TrlKeys.noResumeLink]}
                            </BlokoLink>
                        ),
                    })}
                </Text>
            </div>
        );
    }

    return (
        <AttachWrapper
            activatorRef={buttonRef}
            placement={InfoPlacement.BottomStart}
            theme={InfoTheme.Light}
            showCloseButton={false}
            render={(close) => {
                return (
                    <>
                        <Text>{trls[TrlKeys.attachInfo]}</Text>
                        <VSpacing base={4} />
                        <Form
                            onSubmit={(event) => {
                                onSubmit(event);
                                close?.();
                            }}
                        >
                            {resumes?.resumeIdentifiers.map((resume) => {
                                return (
                                    <React.Fragment key={resume.id}>
                                        <input type="hidden" value={resume.id} name="resumeIdentifier" />
                                        <Checkbox
                                            defaultChecked={resumes?.resumeIdsProfTestAttached.includes(resume.id)}
                                            name="attachResumeId"
                                            value={resume.id}
                                        >
                                            {resume.title}
                                        </Checkbox>
                                        <VSpacing base={2} />
                                    </React.Fragment>
                                );
                            })}
                            <VSpacing base={4} />
                            <div className={`${styles.buttonsContainer} ${styles.buttonsContainerEnd}`}>
                                <Button
                                    stretched={stretched}
                                    kind={ButtonKind.Primary}
                                    type={ButtonType.Submit}
                                    data-qa="attach-resume-save"
                                >
                                    {trls[TrlKeys.attachSave]}
                                </Button>
                                <Button stretched={stretched} appearance={ButtonAppearance.Outlined} onClick={close}>
                                    {trls[TrlKeys.attachCancel]}
                                </Button>
                            </div>
                        </Form>
                    </>
                );
            }}
        >
            <Button
                innerRef={buttonRef}
                disabled={fetching}
                loading={fetching && <Loading />}
                kind={ButtonKind.Primary}
                onClick={attachToggle}
                stretched={stretched}
                data-qa={
                    resumes.resumeIdentifiers.length === 1 && resumes.resumeIdsProfTestAttached.length === 1
                        ? 'detach-resume'
                        : 'attach-resume'
                }
            >
                {getAttachButtonText()}
            </Button>
        </AttachWrapper>
    );
};

export default translation(AttachResume);
